import { useMemo, useState } from "preact/hooks";

import type { AssigneeOption } from "../components/AssigneePickerSheet";
import { ProjectFormSheet } from "../components/ProjectFormSheet";
import { ProjectTasksChecklist } from "../components/ProjectTasksChecklist";
import { RecordDetailOverlay } from "../components/RecordDetailOverlay";
import { LeaveProjectControl, WhosHelping } from "../components/WhosHelping";
import { useProjectShares } from "../hooks/useProjectShares";
import { useProjectBids } from "../hooks/useProjects";
import { readCachedIdentity } from "../lib/meCache";
import {
  archiveProjectConfirmTitle,
  ARCHIVE_PROJECT_BUTTON_LABEL,
  ARCHIVE_PROJECT_CONFIRM_BODY,
  ARCHIVE_PROJECT_CONFIRM_LABEL,
  isEditedRecord,
  PROJECT_ARCHIVED_TAG_LABEL,
  PROJECT_QUOTES_HEADING,
  projectStatusLabel,
  quoteStatusLabel,
  RESTORE_PROJECT_BUTTON_LABEL,
  SAVED_MESSAGE,
} from "../lib/recordsCopy";
import type { ProjectOut } from "../types";

export interface ProjectDetailOverlayProps {
  project: ProjectOut;
  /** Quotes (bids) are admin/api ONLY, server-side — never manager, never
   * member (ADR-002 §5/§11). This must gate BOTH the render below AND
   * whether `useProjectBids` ever fetches at all (its own `enabled`
   * argument) — a manager/member must never issue this request in the
   * first place, not just have the result hidden once it arrives. Also
   * gates the Edit/Archive/Restore write affordances — a stricter gate
   * than the project's own read access (admin/manager both view;
   * admin/api only writes, ADR-002 A6 decision 7.1). */
  isAdmin: boolean;
  onClose: () => void;
  onNotify: (message: string) => void;
  /** Sprint-010 C4 (design-phase-c.md §3.1), widened sprint-013 C2 (SA
   * ruling Decision 2.5) to also cover `name` — `PATCH /projects/{id}`,
   * threaded from the parent's `useProjects().patchProject` so the
   * fetched list array (this component's `project` prop is derived from
   * it via `.find()`) stays the single source of truth. */
  onSaveProject: (
    projectId: string,
    body: { name?: string; summary?: string | null; status?: string | null },
  ) => Promise<void>;
  /** Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5) — the
   * Archive confirm's submit, threaded from `useProjects().archiveProject`.
   * The caller (`ProjectsView`) is responsible for closing this overlay
   * and showing the snackbar on success, mirroring `AnimalDetailOverlay`'s
   * `onArchive` contract exactly. Optional so pre-existing callers across
   * the test suite that don't exercise Archive don't all need a value. */
  onArchive?: () => Promise<void>;
  /** Sprint-013 C2 (sa-ruling-crud-endpoints-2026-07-10.md Decision 2.4) —
   * the Restore action's submit, threaded from
   * `useProjects().restoreProject`. Unlike `onArchive`, this does NOT go
   * through a `ConfirmDialog` (restoring is reversible/non-destructive —
   * see `lib/recordsCopy.ts`'s own note on this). Optional for the same
   * reason `onArchive` is. */
  onRestore?: () => Promise<void>;
  /** Sprint-017 E7 — the signed-in user's id (for the helper's "Leave this
   * project" self-remove, and to avoid picking themselves). Optional (default
   * `""`) so pre-existing project tests that predate E7 keep compiling. */
  meId?: string;
  /** ADR-003 §3.2 `can_assign_out` (admin/manager) — drives whether the
   * checklist's trailing assignee chip is interactive. Default `false`. */
  canAssignOut?: boolean;
  /** People for the assign + "who's helping" pickers (admin/manager
   * sessions carry a roster via the team board; empty otherwise). */
  roster?: AssigneeOption[];
}

/**
 * Sprint-005 C4 — a project's detail screen: name, status, notes, and its
 * to-do checklist if one exists. Reached only by admin/manager (a member
 * never sees the Projects list at all — `GET /projects` 403s for member,
 * ADR-002 §5/§11 — see `ProjectsView`'s gating). The Quotes section is a
 * further, stricter gate on top of that: admin only, never manager.
 *
 * Sprint-010 C4 (design-phase-c.md §3.1) — adds the summary/status edit
 * sheet, admin/api only, and the record-level "Edited" chip (RESOLVED
 * R-C0b-1), visible to both admin and manager — whoever can already view
 * the project at all (a member never reaches this overlay in the first
 * place).
 *
 * Sprint-013 C2 (design-spec-animal-cards-2026-07-10.md §5, sa-ruling-
 * crud-endpoints-2026-07-10.md Decision 2, mirroring the C1 Animals CRUD
 * precedent) — the Edit sheet widens to `name` (via `ProjectFormSheet`,
 * replacing the old summary/status-only `EditProjectSheet`), and this
 * overlay gains Archive (`ConfirmDialog`) and Restore, both admin-only.
 * The "Archived" tag is read-visible to every role that can already view
 * the project (same posture as the "Edited" chip), independent of the
 * write affordances themselves.
 */
export function ProjectDetailOverlay({
  project,
  isAdmin,
  onClose,
  onNotify,
  onSaveProject,
  onArchive,
  onRestore,
  meId = "",
  canAssignOut = false,
  roster = [],
}: ProjectDetailOverlayProps) {
  const bids = useProjectBids(project.id, isAdmin);
  const statusLabel = projectStatusLabel(project.status);
  const isArchived = project.archived_at != null;
  const [editing, setEditing] = useState(false);

  // ADR-003 §3.2 `can_share` — only the owner (+ admin/api) manages
  // "who's helping"; a shared-in helper sees no roster, only "Leave this
  // project" (Q5). A member owner reaches share management the same way.
  const canShare = isAdmin || project.is_owner === true;
  const isHelper = !isAdmin && project.is_owner === false;

  // Sprint-018 E4 (TD-044) — a MEMBER owner (not admin, not manager —
  // `canAssignOut` mirrors `can_hand_out_work`, true for admin+manager,
  // false for a plain member). The `roster` prop below is team-board-
  // sourced and empty for exactly this session (see its own doc comment),
  // so `WhosHelping` swaps its picker's data source to `GET
  // /me/shareable-users` only for this case — managers/admins are
  // unaffected (`isMemberOwner` stays false for them).
  const isMemberOwner = canShare && !isAdmin && !canAssignOut;

  // The helper list is fetched ONCE here (owner/admin only; `GET
  // /projects/{id}/shares` is `can_share`-gated) and shared by "Who's
  // helping" and the checklist's assignee names.
  const shares = useProjectShares(project.id, canShare);

  // Who the checklist can name. The picker `roster` is team-board-sourced
  // (empty for a member session) and also has the signed-in person
  // filtered out, so a task assigned to yourself used to render "?".
  // Names come only from what this view already has: the unfiltered
  // roster, the helper list, and the signed-in person's own identity
  // (the persisted `/me`, since this overlay is only handed `meId`).
  // A shared-in helper (not owner) can read neither the team board nor
  // the helper list, so other people's names stay unknown there and the
  // chip shows a neutral person glyph; closing that needs a member-scoped
  // project roster on the backend (TD-044), deliberately not built here.
  const assigneeNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const share of shares.shares) if (share.name) names[share.user_id] = share.name;
    for (const person of roster) names[person.id] = person.name;
    const self = readCachedIdentity();
    if (meId && self && self.id === meId) names[meId] = self.name;
    return names;
  }, [shares.shares, roster, meId]);

  return (
    <RecordDetailOverlay
      onClose={onClose}
      title={project.name}
      backLabel="Back to Projects"
      isAdmin={isAdmin}
      isArchived={isArchived}
      showEditedChip={isEditedRecord(project.created_at, project.updated_at)}
      archivedTagLabel={PROJECT_ARCHIVED_TAG_LABEL}
      onEdit={isAdmin ? () => setEditing(true) : undefined}
      onArchive={onArchive}
      onRestore={onRestore}
      archiveConfirm={{
        title: archiveProjectConfirmTitle(project.name),
        body: ARCHIVE_PROJECT_CONFIRM_BODY,
        confirmLabel: ARCHIVE_PROJECT_CONFIRM_LABEL,
      }}
      archiveButtonLabel={ARCHIVE_PROJECT_BUTTON_LABEL}
      restoreButtonLabel={RESTORE_PROJECT_BUTTON_LABEL}
      onNotify={onNotify}
    >
      {/* ── Project body slot (Sprint-017 E7) ──────────────────────────
       * Summary + the editable project-tasks checklist (replacing the
       * display-only `punch_list` render) + the owner-only "Who's helping"
       * section + a helper's "Leave this project". Touches ONLY this body
       * region — the shared chrome above (header / edited-chip / Archive↔
       * Restore) is owned by E1 (`RecordDetailOverlay`) and is not
       * re-entered. The roll-up pill renders at the checklist's body-top
       * (design §3.7); the header-chrome mirror is intentionally out of
       * scope for E7 (it lives in E1's chrome). The Quotes section
       * (admin-only) and the Edit `ProjectFormSheet` also live here.
       * ─────────────────────────────────────────────────────────────── */}
      {statusLabel ? <p class="admin-row-sub">{statusLabel}</p> : null}
      {project.summary ? (
        <p class="task-title" style={{ marginTop: "10px" }}>
          {project.summary}
        </p>
      ) : null}

      <ProjectTasksChecklist
        projectId={project.id}
        canAssignOut={canAssignOut}
        roster={roster.filter((p) => p.id !== meId)}
        assigneeNames={assigneeNames}
      />

      {canShare ? (
        <WhosHelping
          projectId={project.id}
          projectName={project.name}
          roster={roster.filter((p) => p.id !== meId)}
          isMemberOwner={isMemberOwner}
          onNotify={onNotify}
          shares={shares}
        />
      ) : null}

      {isHelper ? (
        <LeaveProjectControl
          projectId={project.id}
          projectName={project.name}
          meId={meId}
          onLeft={(message) => {
            onNotify(message);
            onClose();
          }}
        />
      ) : null}

      {isAdmin ? (
        <>
          <h2 class="admin-section-heading" style={{ marginTop: "22px" }}>
            {PROJECT_QUOTES_HEADING}
          </h2>
          {bids.loading ? (
            <p class="loading-hint">Loading…</p>
          ) : bids.error ? (
            <p class="error-hint" role="alert">
              {bids.error}
            </p>
          ) : bids.bids && bids.bids.length === 0 ? (
            <p class="empty-hint">No quotes yet.</p>
          ) : (
            <ul class="admin-list">
              {bids.bids?.map((bid) => (
                <li class="quote-row" key={bid.id}>
                  <span class="quote-contractor">
                    {bid.contractor_name ?? "Unknown contractor"}
                  </span>
                  <span class="quote-status">
                    {quoteStatusLabel(bid.status)}
                    {bid.amount != null ? ` · $${bid.amount.toLocaleString()}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {isAdmin ? (
        <ProjectFormSheet
          open={editing}
          mode="edit"
          project={project}
          onCancel={() => setEditing(false)}
          onSave={async (fields) => {
            await onSaveProject(project.id, fields);
            setEditing(false);
            onNotify(SAVED_MESSAGE);
          }}
        />
      ) : null}
    </RecordDetailOverlay>
  );
}
